import { useState } from "react";
import { Zap, Wallet, ShieldCheck, Radar, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { phantomSignIn } from "@/hooks/usePhantom";

const EmailAuth = ({ onAuth }) => {
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(mode === "signup" ? "/auth/register" : "/auth/login", form);
      onAuth(data.token);
    } catch (err) {
      toast.error(errMsg(err, mode === "signup" ? "Sign up failed" : "Login failed"));
    }
    setBusy(false);
  };
  const input = "w-full bg-[#07090D] border border-[#1E293B] px-3 py-2.5 text-sm font-mono outline-none focus:border-[#14F195]/60 transition-colors";
  return (
    <div className="panel p-6 rise" style={{ animationDelay: "120ms" }} data-testid="email-auth-card">
      <div className="flex border border-[#1E293B] mb-5">
        {[["signup", "Sign up"], ["login", "Log in"]].map(([k, l]) => (
          <button key={k} type="button" data-testid={`auth-tab-${k}`} onClick={() => setMode(k)}
            className={`flex-1 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${mode === k ? "bg-[#14F195] text-[#07090D]" : "text-slate-400 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="auth-email-input" className={input} type={mode === "signup" ? "email" : "text"} placeholder="Email" autoComplete="email"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input data-testid="auth-password-input" className={input} type="password" placeholder={mode === "signup" ? "Password (min 6 characters)" : "Password"}
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <button data-testid="auth-submit-btn" disabled={busy || !form.email || !form.password}
          className="w-full py-2.5 bg-[#14F195] text-[#07090D] font-bold text-sm hover:bg-[#0fd884] disabled:opacity-40 active:scale-[0.98] transition-[background-color,transform]">
          {busy ? "Please wait…" : mode === "signup" ? "Create account" : "Log in"}
        </button>
      </form>
      <div className="my-4 flex items-center gap-3 text-[10px] font-mono text-slate-500"><div className="h-px flex-1 bg-[#1E293B]" />OR<div className="h-px flex-1 bg-[#1E293B]" /></div>
      <div className="space-y-2">
        <GoogleButton />
        <PhantomButton onAuth={onAuth} />
      </div>
    </div>
  );
};

const GoogleButton = () => {
  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button data-testid="google-signin-btn" type="button" onClick={signIn}
      className="w-full flex items-center justify-center gap-2 py-2.5 bg-white text-[#07090D] text-sm font-semibold hover:bg-slate-200 active:scale-[0.98] transition-[background-color,transform]">
      <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
      Continue with Google
    </button>
  );
};

const PhantomButton = ({ onAuth }) => {
  const [busy, setBusy] = useState(false);
  const signIn = async () => {
    setBusy(true);
    try {
      const data = await phantomSignIn();
      onAuth(data.token);
    } catch (e) {
      toast.error(errMsg(e, "Connection rejected"));
    }
    setBusy(false);
  };
  return (
    <button data-testid="phantom-signin-btn" onClick={signIn} disabled={busy}
      className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#9945FF] text-white text-sm font-semibold hover:bg-[#8a3df0] active:scale-[0.98] disabled:opacity-60 transition-[background-color,transform]">
      <Wallet className="w-4 h-4" /> {busy ? "Waiting for Phantom…" : "Continue with Phantom"}
    </button>
  );
};

const FEATURES = [
  [Radar, "Tracks GMGN, Axiom + FOMO wallets", "Top Solana meme traders by PnL, rug and honeypot tokens filtered out, refreshed every 30 minutes."],
  [TrendingUp, "Copies their buys for you", "Your own bot wallet mirrors their entries through Raydium or pump.fun. You choose the size."],
  [ShieldCheck, "Auto exits", "Every copy sells the moment the trader you copy sells, so you're in and out right alongside them."],
];

export default function Landing({ onAuth }) {
  return (
    <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-10 min-h-screen flex flex-col" data-testid="landing-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative w-9 h-9 bg-[#14F195] flex items-center justify-center">
            <Zap className="w-4 h-4 text-[#07090D] absolute -translate-x-1" strokeWidth={3} />
            <Zap className="w-4 h-4 text-[#9945FF] absolute translate-x-1" strokeWidth={3} />
          </div>
          <span className="font-display text-xl font-extrabold tracking-tight">DUAL BOOST</span>
        </div>
      </div>
      <div className="flex-1 grid lg:grid-cols-12 gap-10 items-center py-14">
        <div className="lg:col-span-7 rise">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#14F195]/40 text-[#14F195] text-[11px] font-mono tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-[#14F195] pulse-dot" /> ENGINE LIVE 24/7
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95]">
            Copy Solana's top <span className="text-[#14F195]">1%</span><br />meme traders.<br />
            <span className="text-[#9945FF]">Automatically.</span>
          </h1>
          <p className="mt-6 text-base text-slate-400 max-w-lg">
            Create an account, fund your personal bot wallet, and Dual Boost copies trades from the highest-PnL wallets on GMGN.ai, Axiom and FOMO, around the clock.
          </p>
          <div className="mt-6 font-mono text-sm" data-testid="price-tag">
            <span className="text-2xl font-bold text-white">$9.99</span><span className="text-slate-400"> / month in SOL</span>
          </div>
          <div className="mt-8 grid sm:grid-cols-3 gap-px bg-[#1E293B] border border-[#1E293B]">
            {FEATURES.map(([Icon, title, body]) => (
              <div key={title} className="bg-[#0B0F17] p-4">
                <Icon className="w-4 h-4 text-[#14F195]" />
                <div className="mt-2 font-display text-sm font-bold">{title}</div>
                <div className="mt-1 text-xs text-slate-400">{body}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <EmailAuth onAuth={onAuth} />
          <p className="mt-3 text-[11px] font-mono text-slate-500">Free to sign up and look around. Subscribe to activate the copy engine.</p>
        </div>
      </div>
      <p className="text-[10px] font-mono text-slate-500">Not financial advice. Meme coins are highly volatile; copy trading can lose 100% of funds.</p>
    </div>
  );
}
